// @ts-check

/**
 * @file Renders the timelogs dashboard screen.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ActivityIndicator, Pressable, ScrollView, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {optionalString} from "typanic"
import {Timelog} from "../../src/frontend-models/timelog"
import errorMessage from "../../src/shared/error-message"
import useCurrentUser from "../../src/auth/use-current-user"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"
import TimelogsDashboardBillingProgressCard from "../../src/screens/timelogs/dashboard/billing-progress-card"
import TimelogsDashboardDateRangeModal from "../../src/screens/timelogs/dashboard/date-range-modal"
import TimelogsDashboardDeleteConfirmModal from "../../src/screens/timelogs/dashboard/delete-confirm-modal"
import TimelogsDashboardFilters from "../../src/screens/timelogs/dashboard/dashboard-filters"
import TimelogsDashboardLogTimeModal from "../../src/screens/timelogs/dashboard/log-time-modal"
import {timelogComment, timelogDateString, timelogId, timelogProjectName, timelogTaskId, timelogTaskName, timelogTimeSeconds, timelogUserEmail, timelogUserFirstName, timelogUserId, timelogUserLastName} from "../../src/screens/timelogs/dashboard/log-model"
import TimelogsDashboardLogSection from "../../src/screens/timelogs/dashboard/log-section"
import TimelogsDashboardMoreFiltersModal from "../../src/screens/timelogs/dashboard/more-filters-modal"
import TimelogsDashboardSummaryCard from "../../src/screens/timelogs/dashboard/summary-card"
import ScreenTitle from "../../src/components/screen-title"
import {durationSecondsToInput, parseDurationSeconds} from "../../src/shared/duration"
import {pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @type {Record<string, object>} */
const styles = {}
const TIMELOGS_DASHBOARD_QUERY_PARAM_SELECTOR = [
  "date_gteq",
  "date_lteq",
  "not_invoiced",
  "only_invoiced",
  "search_query",
  "task_project_id_eq_any",
  "user_id_eq_any"
]

/** @typedef {Awaited<ReturnType<typeof Timelog.dashboardSummary>>} TimelogDashboardSummary */
/** @typedef {Awaited<ReturnType<typeof Timelog.dashboardLogs>>[number]} TimelogDashboardLog */
/** @typedef {Awaited<ReturnType<typeof Timelog.dashboardFilterOptions>>} TimelogDashboardFilterOptions */
/** @typedef {TimelogDashboardFilterOptions["projects"][number]} TimelogDashboardFilterOption */

/**
 * @typedef {object} TimelogDashboardFilters
 * @property {string | undefined} dateFrom Start date filter.
 * @property {string | undefined} dateTo End date filter.
 * @property {boolean} notInvoiced Whether only uninvoiced logs are shown.
 * @property {boolean} onlyInvoiced Whether only invoiced logs are shown.
 * @property {string[]} projectIds Selected project identifiers.
 * @property {string | undefined} searchQuery Search text filter.
 * @property {string[]} userIds Selected user identifiers.
 */

/**
 * @typedef {object} TimelogDashboardLogSection
 * @property {string} id Stable section identifier.
 * @property {string} label Section heading.
 * @property {TimelogDashboardLog[]} timelogs Timelogs in the section.
 */

/**
 * @typedef {object} TimelogDashboardCommandParams
 * @property {string} [dateFrom] Start date command filter.
 * @property {string} [dateTo] End date command filter.
 * @property {boolean} [notInvoiced] Whether only uninvoiced logs are requested.
 * @property {boolean} [onlyInvoiced] Whether only invoiced logs are requested.
 * @property {string[]} [projectIds] Project identifiers to include.
 * @property {string[]} [userIds] User identifiers to include.
 */

/** @typedef {Record<string, never>} TimelogsDashboardRouteProps */

/**
 * @typedef {object} TimelogsDashboardRouteState
 * @property {{kind: "error", text: string} | null} actionStatus Error shown for destructive actions.
 * @property {string} confirmingDeleteTimelogId Timelog id pending delete confirmation.
 * @property {boolean} deletingTimelog Whether a delete request is in progress.
 * @property {string} editingTimelogId Timelog id currently being edited.
 * @property {{projects: TimelogDashboardFilterOption[], users: TimelogDashboardFilterOption[]}} filterOptions Options shown in dashboard filters.
 * @property {TimelogDashboardFilters} filters Active dashboard filters.
 * @property {boolean} loading Whether dashboard data is loading.
 * @property {boolean} markingInvoiced Whether the bulk invoicing action is running.
 * @property {boolean} savingTimelog Whether the log-time form is submitting.
 * @property {boolean} showLogTimeModal Whether the log-time modal is visible.
 * @property {boolean} showMoreFiltersModal Whether the more-filters modal is visible.
 * @property {boolean} showDateRangeModal Whether the date-range modal is visible.
 * @property {{kind: "error", text: string} | null} status Error shown for dashboard loading.
 * @property {{kind: "error", text: string} | null} timelogEditorStatus Error shown inside the log-time modal.
 * @property {string} summaryDateFrom Dashboard period start date.
 * @property {string} summaryDateTo Dashboard period end date.
 * @property {string | null} summaryEstimatedEarningsCurrency Currency code used for monetary totals.
 * @property {number} summaryEstimatedEarningsProjected Projected earnings for the active period.
 * @property {number} summaryEstimatedEarningsTotal Total estimated earnings for the active period.
 * @property {number} summaryEstimatedEarningsTrendPercent Earnings trend against the previous period.
 * @property {number} summaryHoursTotal Logged hours for the active period.
 * @property {number} summaryHoursTrendPercent Logged-hours trend against the previous period.
 * @property {number} summaryInvoicedAmount Amount already marked as invoiced.
 * @property {number} summaryInvoicedPercent Percentage of earnings already invoiced.
 * @property {boolean} summaryLoaded Whether aggregate metrics are ready for rendering.
 * @property {number} summaryTotalCount Number of logs returned by the dashboard query.
 * @property {string | null} summaryTrendPeriodFrom Comparison period start date.
 * @property {string | null} summaryTrendPeriodTo Comparison period end date.
 * @property {TimelogDashboardLog[]} timelogs Loaded dashboard logs.
 */

/**
 * Timelogs dashboard filterable read screen.
 * @augments {ShapeComponent<TimelogsDashboardRouteProps, TimelogsDashboardRouteState>}
 */
class TimelogsDashboardRoute extends ShapeComponent {
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteQueryParams} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useCurrentUser>["user"]} */
  currentUser
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {TimelogDashboardFilters} */
  routeFilters
  /** @type {string} */
  routeFilterKey
  hasInitializedRouteFilters = false
  loadRequestId = 0
  /** @type {ReturnType<typeof setTimeout> | null} */
  searchDebounceTimer = null

  /** @type {TimelogsDashboardRouteState} */
  state = {
    actionStatus: /** @type {{kind: "error", text: string} | null} */ null,
    confirmingDeleteTimelogId: "",
    deletingTimelog: false,
    editingTimelogId: "",
    filterOptions:
      /** @type {{projects: TimelogDashboardFilterOption[], users: TimelogDashboardFilterOption[]}} */ {
        projects: [],
        users: []
      },
    filters: /** @type {TimelogDashboardFilters} */ ({
      dateFrom: undefined,
      dateTo: undefined,
      notInvoiced: false,
      onlyInvoiced: false,
      projectIds: [],
      searchQuery: undefined,
      userIds: []
    }),
    loading: true,
    markingInvoiced: false,
    savingTimelog: false,
    showLogTimeModal: false,
    showMoreFiltersModal: false,
    showDateRangeModal: false,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    timelogEditorStatus:
      /** @type {{kind: "error", text: string} | null} */ null,
    summaryDateFrom: "",
    summaryDateTo: "",
    summaryEstimatedEarningsCurrency: null,
    summaryEstimatedEarningsProjected: 0,
    summaryEstimatedEarningsTotal: 0,
    summaryEstimatedEarningsTrendPercent: 0,
    summaryHoursTotal: 0,
    summaryHoursTrendPercent: 0,
    summaryInvoicedAmount: 0,
    summaryInvoicedPercent: 0,
    summaryLoaded: false,
    summaryTotalCount: 0,
    summaryTrendPeriodFrom: null,
    summaryTrendPeriodTo: null,
    timelogs: /** @type {TimelogDashboardLog[]} */ []
  }

  /**
   * Wires the locale, route, and auth hooks for the dashboard, plus
   * the route-driven dashboard load effects. ShapeComponent calls
   * this once per render at top level, so it is the only place where
   * hook calls are valid for the screen.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(TIMELOGS_DASHBOARD_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()
    const currentUser = useCurrentUser().user
    const colors = useThemeColors()
    const themeName = useThemeName()
    const routeFilters = filtersFromParams(params)
    const routeFilterKey = serializeFilters(routeFilters)

    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    this.currentUser = currentUser
    this.colors = colors
    this.themeName = themeName
    this.routeFilters = routeFilters
    this.routeFilterKey = routeFilterKey
    if (!this.hasInitializedRouteFilters) {
      this.state.filters = routeFilters
      this.hasInitializedRouteFilters = true
    }
    const currentRouteFilterKey = routeFilterKey
    useEffect(() => {
      const nextFilters = this.tt.routeFilters
      const currentFilters = this.s.filters
      if (
        this.s.summaryLoaded &&
        serializeFilters(currentFilters) === serializeFilters(nextFilters)
      ) {
        return
      }
      if (
        this.s.summaryLoaded &&
        filtersWithoutSearchMatch(currentFilters, nextFilters)
      ) {
        this.s.filters = nextFilters
        return
      }
      const requestId = ++this.loadRequestId
      this.s.filters = nextFilters
      this.s.loading = true
      this.s.status = null
      void this.loadDashboard(requestId, nextFilters)
    }, [currentRouteFilterKey])
    useEffect(() => {
      return () => {
        this.loadRequestId += 1
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer)
        }
      }
    }, [])
  }

  /**
   * Renders the dashboard screen and modal stack.
   * @returns {ReactNode} Dashboard screen content.
   */
  render() {
    const {
      actionStatus,
      confirmingDeleteTimelogId,
      deletingTimelog,
      filterOptions,
      filters,
      loading,
      markingInvoiced,
      savingTimelog,
      showDateRangeModal,
      showLogTimeModal,
      showMoreFiltersModal,
      status,
      summaryEstimatedEarningsTrendPercent,
      summaryHoursTotal,
      summaryHoursTrendPercent,
      summaryInvoicedPercent,
      summaryLoaded,
      summaryTotalCount,
      timelogEditorStatus
    } = this.s
    const visibleTimelogs = this.visibleTimelogs()
    const logSections = this.logSections(visibleTimelogs)
    const markInvoicedDisabled = markingInvoiced || this.s.timelogs.length === 0
    return (
      <View
        style={styles.screen ||= {
          flex: 1,
          position: "relative"
        }}
        testID="timelogsDashboardScreen"
      >
        <ScreenTitle>{_("Time Logs Dashboard")}</ScreenTitle>
        <ScrollView
          style={styles.scroll ||= {
            flex: 1
          }}
          testID="timelogsDashboardScreen/scroll"
        >
          <View
            style={styles.content ||= {
              padding: 24
            }}
            testID="timelogsDashboardScreen/content"
          >
            <View
              style={styles.header ||= {
                gap: 12,
                marginBottom: 24
              }}
              testID="timelogsDashboardScreen/header"
            >
              <View
                style={styles.headerTopRow ||= {
                  alignItems: "flex-start",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12,
                  justifyContent: "space-between"
                }}
                testID="timelogsDashboardScreen/headerTopRow"
              >
                {[
                  <View
                    key="text"
                    style={styles.headerText ||= {
                      gap: 6
                    }}
                    testID="timelogsDashboardScreen/headerText"
                  >
                    {[
                      <Text
                        key="subtitle"
                        style={styles[`subtitle-${this.tt.themeName}`] ||= {
                          color: this.tt.colors.textMuted,
                          fontSize: 14,
                          fontWeight: 600
                        }}
                        testID="timelogsDashboardScreen/subtitle"
                      >
                        {_("Manage and track team billable hours")}
                      </Text>,
                      <Text
                        key="title"
                        style={styles[`title-${this.tt.themeName}`] ||= {
                          color: this.tt.colors.text,
                          fontSize: 28,
                          fontWeight: 700
                        }}
                        testID="timelogsDashboardScreen/title"
                      >
                        {_("Time Logs Dashboard")}
                      </Text>
                    ]}
                  </View>,
                  <View
                    key="actions"
                    style={styles.headerActions ||= {
                      alignItems: "center",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 10
                    }}
                    testID="timelogsDashboardScreen/headerActions"
                  >
                    <Pressable
                      disabled={markInvoicedDisabled}
                      onPress={this.tt.onMarkInvoicedPress}
                      style={styles[`markInvoicedButton-${this.tt.themeName}-${markInvoicedDisabled}`] ||= {
                        alignItems: "center",
                        backgroundColor:
                          markInvoicedDisabled
                            ? this.tt.colors.disabledBackground
                            : this.tt.colors.secondary,
                        borderRadius: 10,
                        flexDirection: "row",
                        paddingHorizontal: 14,
                        paddingVertical: 10
                      }}
                      testID="timelogsDashboardScreen/markInvoicedButton"
                    >
                      <View
                        style={styles.markInvoicedButtonIcon ||= {
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 8,
                          width: 16
                        }}
                        testID="timelogsDashboardScreen/markInvoicedButton/icon"
                      >
                        <FontAwesome6
                          color={
                            markInvoicedDisabled
                              ? this.tt.colors.textMuted
                              : this.tt.colors.onSecondary
                          }
                          name="circle-check"
                          size={14}
                        />
                      </View>
                      <Text
                        style={styles[`markInvoicedButtonText-${this.tt.themeName}-${markInvoicedDisabled}`] ||= {
                          color: markInvoicedDisabled
                            ? this.tt.colors.textMuted
                            : this.tt.colors.onSecondary,
                          fontSize: 14,
                          fontWeight: 700
                        }}
                        testID="timelogsDashboardScreen/markInvoicedButton/text"
                      >
                        {markingInvoiced
                          ? _("Marking...")
                          : _("Mark all invoiced")}
                      </Text>
                    </Pressable>
                    <Pressable
                      onPress={this.tt.onAddPress}
                      style={styles.addButton ||= {
                        alignItems: "center",
                        backgroundColor: "#1d4ed8",
                        borderRadius: 10,
                        flexDirection: "row",
                        paddingHorizontal: 14,
                        paddingVertical: 10
                      }}
                      testID="timelogsDashboardScreen/addButton"
                    >
                      <View
                        style={styles.addButtonIcon ||= {
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 8,
                          width: 16
                        }}
                        testID="timelogsDashboardScreen/addButton/icon"
                      >
                        <FontAwesome6 color="#ffffff" name="plus" size={14} />
                      </View>
                      <Text
                        style={styles.addButtonText ||= {
                          color: "#ffffff",
                          fontSize: 14,
                          fontWeight: 700
                        }}
                        testID="timelogsDashboardScreen/addButton/text"
                      >
                        {_("Log time")}
                      </Text>
                    </Pressable>
                  </View>
                ]}
              </View>
            </View>
            <TimelogsDashboardFilters
              dateRangeLabel={this.dateRangeLabel()}
              onDateRangePress={this.tt.onDateRangePress}
              onMoreFiltersPress={this.tt.onMoreFiltersPress}
              onProjectPress={this.tt.onProjectPress}
              onSearchChangeText={this.tt.onSearchChangeText}
              onStatusFilterClearPress={this.tt.onStatusFilterClearPress}
              onUserPress={this.tt.onUserPress}
              secondaryFilterLabel={this.secondaryFilterLabel()}
              projectIds={filters.projectIds}
              projectOptions={this.projectOptions(
                filterOptions.projects,
                filters.projectIds
              )}
              searchQuery={filters.searchQuery || ""}
              statusFilterValue={this.statusFilterValue()}
              userIds={filters.userIds}
              userOptions={this.userOptions(
                filterOptions.users,
                filters.userIds
              )}
            />
            {actionStatus &&
              <View
                style={styles.actionStatusCard ||= {
                  backgroundColor: "#fef2f2",
                  borderColor: "#fecaca",
                  borderRadius: 12,
                  borderWidth: 1,
                  marginTop: 20,
                  padding: 16
                }}
                testID="timelogsDashboardScreen/actionStatus"
              >
                <Text
                  style={styles.actionStatusText ||= {
                    color: "#b91c1c",
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="timelogsDashboardScreen/actionStatusText"
                >
                  {actionStatus.text}
                </Text>
              </View>
            }
            {loading &&
              <View
                style={styles.loadingState ||= {
                  alignItems: "center",
                  gap: 12,
                  paddingVertical: 48
                }}
                testID="timelogsDashboardScreen/loading"
              >
                <ActivityIndicator color="#0ea5e9" size="small" />
                <Text
                  style={styles.loadingText ||= {
                    color: "#334155",
                    fontSize: 15,
                    fontWeight: 600
                  }}
                  testID="timelogsDashboardScreen/loadingText"
                >
                  {_("Loading dashboard...")}
                </Text>
              </View>
            }
            {status &&
              <View
                style={styles.statusCard ||= {
                  backgroundColor: "#fef2f2",
                  borderColor: "#fecaca",
                  borderRadius: 12,
                  borderWidth: 1,
                  marginTop: 20,
                  padding: 16
                }}
                testID="timelogsDashboardScreen/status"
              >
                <Text
                  style={styles.statusText ||= {
                    color: "#b91c1c",
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="timelogsDashboardScreen/statusText"
                >
                  {status.text}
                </Text>
              </View>
            }
            {!loading && !status && summaryLoaded &&
              <View
                style={styles.summarySection ||= {
                  gap: 20,
                  marginTop: 20
                }}
                testID="timelogsDashboardScreen/summary"
              >
                <Text
                  style={styles.periodText ||= {
                    color: "#475569",
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="timelogsDashboardScreen/period"
                >
                  {this.periodLabel()}
                </Text>
                <View
                  style={styles.summaryGrid ||= {
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 16
                  }}
                  testID="timelogsDashboardScreen/summaryGrid"
                >
                  <TimelogsDashboardSummaryCard
                    accentColor="#0ea5e9"
                    supportingText={this.trendLabel(
                      summaryHoursTrendPercent
                    )}
                    testID="timelogsDashboardScreen/summary/hours"
                    title={_("Total hours")}
                    value={formatHours(summaryHoursTotal)}
                  />
                  <TimelogsDashboardSummaryCard
                    accentColor={
                      summaryEstimatedEarningsTrendPercent >= 0
                        ? "#16a34a"
                        : "#dc2626"
                    }
                    supportingText={this.trendLabel(
                      summaryEstimatedEarningsTrendPercent
                    )}
                    testID="timelogsDashboardScreen/summary/earnings"
                    title={_("Estimated earnings")}
                    value={this.earningsValue()}
                  />
                  <TimelogsDashboardBillingProgressCard
                    description={this.billingProgressDescription()}
                    percent={summaryInvoicedPercent}
                    testID="timelogsDashboardScreen/summary/billingProgress"
                    title={_("Billing progress")}
                  />
                </View>
                {summaryTotalCount === 0 &&
                  <View
                    style={styles[`emptyState-${this.tt.themeName}`] ||= {
                      backgroundColor: this.tt.colors.surface,
                      borderColor: this.tt.colors.border,
                      borderRadius: 12,
                      borderWidth: 1,
                      padding: 16
                    }}
                    testID="timelogsDashboardScreen/emptyState"
                  >
                    <Text
                      style={styles[`emptyStateText-${this.tt.themeName}`] ||= {
                        color: this.tt.colors.textMuted,
                        fontSize: 14,
                        fontWeight: 600
                      }}
                      testID="timelogsDashboardScreen/emptyStateText"
                    >
                      {_("No timelogs found for this period.")}
                    </Text>
                  </View>
                }
                {summaryTotalCount > 0 && visibleTimelogs.length === 0 &&
                  <View
                    style={styles[`searchEmptyState-${this.tt.themeName}`] ||= {
                      backgroundColor: this.tt.colors.surface,
                      borderColor: this.tt.colors.border,
                      borderRadius: 12,
                      borderWidth: 1,
                      padding: 16
                    }}
                    testID="timelogsDashboardScreen/searchEmptyState"
                  >
                    <Text
                      style={styles[`searchEmptyStateText-${this.tt.themeName}`] ||= {
                        color: this.tt.colors.textMuted,
                        fontSize: 14,
                        fontWeight: 600
                      }}
                      testID="timelogsDashboardScreen/searchEmptyStateText"
                    >
                      {_("No timelogs found")}
                    </Text>
                  </View>
                }
                {visibleTimelogs.length > 0 &&
                  <View
                    style={styles.logsSection ||= {
                      gap: 16
                    }}
                    testID="timelogsDashboardScreen/logs"
                  >
                    {logSections.map((section) => (
                      <TimelogsDashboardLogSection
                        key={section.id}
                        onDeletePress={this.tt.onDeletePress}
                        onEditPress={this.tt.onEditPress}
                        params={this.tt.params}
                        section={section}
                      />
                    ))}
                  </View>
                }
              </View>
            }
          </View>
        </ScrollView>
        <TimelogsDashboardDateRangeModal
          dateFrom={this.dateRangeModalDateFrom()}
          dateTo={this.dateRangeModalDateTo()}
          isVisible={showDateRangeModal}
          onApply={this.tt.onDateRangeApply}
          onClose={this.tt.onDateRangeClose}
        />
        <TimelogsDashboardMoreFiltersModal
          isVisible={showMoreFiltersModal}
          onApply={this.tt.onMoreFiltersApply}
          onClose={this.tt.onMoreFiltersClose}
          statusValue={this.statusFilterValue()}
        />
        <TimelogsDashboardDeleteConfirmModal
          bodyText={this.deleteConfirmBodyText()}
          confirmLabel={_("Delete")}
          deleting={deletingTimelog}
          deletingLabel={_("Deleting...")}
          isVisible={Boolean(confirmingDeleteTimelogId)}
          onCancelPress={this.tt.onDeleteConfirmCancelPress}
          onConfirmPress={this.tt.onDeleteConfirmPress}
          title={_("Delete Time Log?")}
        />
        <TimelogsDashboardLogTimeModal
          canPickUser={Boolean(this.tt.currentUser?.admin)}
          initialComment={this.initialTimelogComment()}
          initialDate={this.initialTimelogDate()}
          initialHours={this.initialTimelogHours()}
          initialTaskId={this.initialTimelogTaskId()}
          initialUserId={this.initialTimelogUserId()}
          isSaving={savingTimelog}
          isVisible={showLogTimeModal}
          mode={this.s.editingTimelogId ? "edit" : "create"}
          onClose={this.tt.onLogTimeModalClose}
          onSubmit={this.tt.onLogTimeModalSubmit}
          statusText={timelogEditorStatus?.text}
        />
      </View>
    )
  }

  /**
   * Loads dashboard summary, logs, filter options, and editor choices.
   * @param {number} requestId Request identifier used to ignore stale responses.
   * @param {TimelogDashboardFilters} filters Filters used for the dashboard request.
   * @returns {Promise<void>} Resolves after dashboard state is updated.
   */
  async loadDashboard(requestId, filters) {
    try {
      const commandParams = dashboardCommandParams(filters)
      const [summary, timelogs, filterOptions] = await Promise.all([
        Timelog.dashboardSummary(commandParams),
        Timelog.dashboardLogs(commandParams),
        Timelog.dashboardFilterOptions({
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo
        })
      ])
      if (requestId !== this.loadRequestId) return
      this.s.actionStatus = null
      this.s.filterOptions = filterOptions
      this.s.loading = false
      this.s.status = null
      this.applyDashboardSummary(summary)
      this.s.timelogs = timelogs
    } catch (error) {
      if (requestId !== this.loadRequestId) return
      this.s.actionStatus = null
      this.s.filterOptions = {
        projects: [],
        users: []
      }
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.clearDashboardSummary()
      this.s.timelogs = []
    }
  }

  /**
   * Stores a loaded dashboard summary on flat route state fields.
   * @param {TimelogDashboardSummary} summary Loaded dashboard summary.
   * @returns {void} Nothing.
   */
  applyDashboardSummary(summary) {
    this.s.summaryDateFrom = summary.dateFrom
    this.s.summaryDateTo = summary.dateTo
    this.s.summaryEstimatedEarningsCurrency = summary.estimatedEarningsCurrency
    this.s.summaryEstimatedEarningsProjected = summary.estimatedEarningsProjected
    this.s.summaryEstimatedEarningsTotal = summary.estimatedEarningsTotal
    this.s.summaryEstimatedEarningsTrendPercent = summary.estimatedEarningsTrendPercent
    this.s.summaryHoursTotal = summary.hoursTotal
    this.s.summaryHoursTrendPercent = summary.hoursTrendPercent
    this.s.summaryInvoicedAmount = summary.invoicedAmount
    this.s.summaryInvoicedPercent = summary.invoicedPercent
    this.s.summaryTotalCount = summary.totalCount
    this.s.summaryTrendPeriodFrom = summary.trendPeriodFrom
    this.s.summaryTrendPeriodTo = summary.trendPeriodTo
    this.s.summaryLoaded = true
  }

  /**
   * Clears dashboard summary state after a failed load.
   * @returns {void} Nothing.
   */
  clearDashboardSummary() {
    this.s.summaryLoaded = false
    this.s.summaryDateFrom = ""
    this.s.summaryDateTo = ""
    this.s.summaryEstimatedEarningsCurrency = null
    this.s.summaryEstimatedEarningsProjected = 0
    this.s.summaryEstimatedEarningsTotal = 0
    this.s.summaryEstimatedEarningsTrendPercent = 0
    this.s.summaryHoursTotal = 0
    this.s.summaryHoursTrendPercent = 0
    this.s.summaryInvoicedAmount = 0
    this.s.summaryInvoicedPercent = 0
    this.s.summaryTotalCount = 0
    this.s.summaryTrendPeriodFrom = null
    this.s.summaryTrendPeriodTo = null
  }

  /**
   * Builds the date range label shown in the filter bar.
   * @returns {string} Formatted active period label.
   */
  dateRangeLabel() {
    if (!this.s.summaryLoaded) {
      return _("Date from")
    }
    return `${formatDate(this.s.summaryDateFrom)} - ${formatDate(this.s.summaryDateTo)}`
  }

  /**
   * Reads the start date used when the date-range modal opens.
   * @returns {string} Active start date filter.
   */
  dateRangeModalDateFrom() {
    if (this.s.filters.dateFrom) return this.s.filters.dateFrom
    if (this.s.summaryLoaded) return this.s.summaryDateFrom
    return ""
  }

  /**
   * Reads the end date used when the date-range modal opens.
   * @returns {string} Active end date filter.
   */
  dateRangeModalDateTo() {
    if (this.s.filters.dateTo) return this.s.filters.dateTo
    if (this.s.summaryLoaded) return this.s.summaryDateTo
    return ""
  }

  /**
   * Reads the active invoice status filter.
   * @returns {"all" | "only_invoiced" | "only_uninvoiced"} Invoice status selection.
   */
  statusFilterValue() {
    if (this.s.filters.onlyInvoiced) return "only_invoiced"
    if (this.s.filters.notInvoiced) return "only_uninvoiced"
    return "all"
  }

  /**
   * Builds the secondary filter label for the active status filter.
   * @returns {string | undefined} Secondary label, or `undefined` when no status filter is active.
   */
  secondaryFilterLabel() {
    const statusFilterValue = this.statusFilterValue()
    if (statusFilterValue === "only_invoiced") return _("Invoiced only")
    if (statusFilterValue === "only_uninvoiced") return _("Uninvoiced only")
    return undefined
  }

  /**
   * Filters visible timelogs by the local search query.
   * @returns {TimelogDashboardLog[]} Logs matching the active search filter.
   */
  visibleTimelogs() {
    const searchQuery = (this.s.filters.searchQuery || "").trim().toLowerCase()
    if (!searchQuery) {
      return this.s.timelogs
    }
    return this.s.timelogs.filter((timelog) => {
      const values = [
        timelogComment(timelog),
        timelogProjectName(timelog),
        timelogTaskName(timelog),
        [timelogUserFirstName(timelog), timelogUserLastName(timelog)]
          .filter(Boolean)
          .join(" ")
          .trim(),
        timelogUserEmail(timelog)
      ]
      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(searchQuery)
      )
    })
  }

  /**
   * Groups timelogs by calendar month for rendering.
   * @param {TimelogDashboardLog[]} timelogs Logs to group.
   * @returns {TimelogDashboardLogSection[]} Grouped log sections.
   */
  logSections(timelogs) {
    /** @type {TimelogDashboardLogSection[]} */
    const sections = []
    /** @type {Record<string, TimelogDashboardLogSection>} */
    const sectionsById = {}
    for (const timelog of timelogs) {
      const date = parseIsoDate(timelogDateString(timelog))
      const id = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
        : "unknown"
      if (!sectionsById[id]) {
        sectionsById[id] = {
          id,
          label: date ? monthYearLabel(date) : _("Unknown date"),
          timelogs: []
        }
        sections.push(sectionsById[id])
      }
      sectionsById[id].timelogs.push(timelog)
    }
    return sections
  }

  /**
   * Formats the dashboard period label.
   * @returns {string} Formatted period label.
   */
  periodLabel() {
    return _("%{from} - %{to}", {
      from: formatDate(this.s.summaryDateFrom),
      to: formatDate(this.s.summaryDateTo)
    })
  }

  /**
   * Formats projected earnings with an optional currency.
   * @returns {string} Formatted earnings value.
   */
  earningsValue() {
    const amount = formatInteger(this.s.summaryEstimatedEarningsProjected)
    if (!this.s.summaryEstimatedEarningsCurrency) return amount
    return `${amount} ${this.s.summaryEstimatedEarningsCurrency}`
  }

  /**
   * Formats a trend percentage with its comparison period.
   * @param {number} percent Percentage change to show.
   * @returns {string} Formatted trend label.
   */
  trendLabel(percent) {
    const range = this.trendRange()
    return _("%{percent} vs %{range}", {
      percent: formatPercent(percent),
      range
    })
  }

  /**
   * Formats the comparison period used by trend labels.
   * @returns {string} Formatted comparison period.
   */
  trendRange() {
    if (!this.s.summaryTrendPeriodFrom || !this.s.summaryTrendPeriodTo) {
      return _("No previous period")
    }
    return _("%{from} - %{to}", {
      from: formatDate(this.s.summaryTrendPeriodFrom),
      to: formatDate(this.s.summaryTrendPeriodTo)
    })
  }

  /**
   * Builds the billing progress summary sentence.
   * @returns {string} Human-readable invoiced total summary.
   */
  billingProgressDescription() {
    return _("Invoiced %{invoiced} of %{total}%{currency}", {
      currency: this.s.summaryEstimatedEarningsCurrency
        ? ` ${this.s.summaryEstimatedEarningsCurrency}`
        : "",
      invoiced: formatInteger(this.s.summaryInvoicedAmount),
      total: formatInteger(this.s.summaryEstimatedEarningsTotal)
    })
  }

  /**
   * Ensures selected project ids are present in the filter options.
   * @param {TimelogDashboardFilterOption[]} options Loaded project options.
   * @param {string[]} selectedIds Selected project ids from the route.
   * @returns {TimelogDashboardFilterOption[]} Project options including selected ids.
   */
  projectOptions(options, selectedIds) {
    return optionsWithSelectedIds(options, selectedIds)
  }

  /**
   * Ensures selected user ids are present in the filter options.
   * @param {TimelogDashboardFilterOption[]} options Loaded user options.
   * @param {string[]} selectedIds Selected user ids from the route.
   * @returns {TimelogDashboardFilterOption[]} User options including selected ids.
   */
  userOptions(options, selectedIds) {
    return optionsWithSelectedIds(options, selectedIds)
  }

  /**
   * Finds the log currently opened in the editor.
   * @returns {TimelogDashboardLog | undefined} Log being edited, if any.
   */
  editingTimelog() {
    return this.s.timelogs.find((timelog) => String(timelogId(timelog)) === this.s.editingTimelogId)
  }

  /**
   * Finds the log currently pending delete confirmation.
   * @returns {TimelogDashboardLog | undefined} Log pending deletion, if any.
   */
  confirmingDeleteTimelog() {
    return this.s.timelogs.find((timelog) => String(timelogId(timelog)) === this.s.confirmingDeleteTimelogId)
  }

  /**
   * Builds the delete confirmation body text.
   * @returns {string} Confirmation body text.
   */
  deleteConfirmBodyText() {
    const confirmingDeleteTimelog = this.confirmingDeleteTimelog()
    if (!confirmingDeleteTimelog) {
      return _(
        "Are you sure you want to delete this log? This action cannot be undone and will be permanently removed from your dashboard."
      )
    }
    return _(
      "Are you sure you want to delete %{title}? This action cannot be undone and will be permanently removed from your dashboard.",
      {
        title:
          timelogTaskName(confirmingDeleteTimelog) ||
          timelogComment(confirmingDeleteTimelog) ||
          _("this log")
      }
    )
  }

  /**
   * Reads the initial comment for the log-time modal.
   * @returns {string} Initial comment text.
   */
  initialTimelogComment() {
    const editingTimelog = this.editingTimelog()

    return editingTimelog ? timelogComment(editingTimelog) : ""
  }

  /**
   * Reads the initial date for the log-time modal.
   * @returns {string} Initial date value.
   */
  initialTimelogDate() {
    const editingTimelog = this.editingTimelog()

    return editingTimelog ? timelogDateString(editingTimelog) || currentIsoDate() : currentIsoDate()
  }

  /**
   * Reads the initial hour value for the log-time modal.
   * @returns {string} Initial hour value.
   */
  initialTimelogHours() {
    const editingTimelog = this.editingTimelog()
    if (!editingTimelog) return ""
    return durationSecondsToInput(timelogTimeSeconds(editingTimelog))
  }

  /**
   * Reads the initial task id for the log-time modal.
   * @returns {string} Selected task identifier.
   */
  initialTimelogTaskId() {
    const editingTimelog = this.editingTimelog()
    const taskId = editingTimelog ? timelogTaskId(editingTimelog) : null
    return taskId ? String(taskId) : ""
  }

  /**
   * Reads the initial user id for the log-time modal.
   * @returns {string} Selected user identifier.
   */
  initialTimelogUserId() {
    const editingTimelog = this.editingTimelog()
    const userId = editingTimelog ? timelogUserId(editingTimelog) : null
    if (userId) return String(userId)
    const fallbackId = this.tt.currentUser?.id
    return fallbackId ? String(fallbackId) : ""
  }

  /**
   * Opens the log-time modal for a new log.
   * @returns {void} Nothing.
   */
  onAddPress = () => {
    this.s.editingTimelogId = ""
    this.s.showLogTimeModal = true
    this.s.timelogEditorStatus = null
  }

  /**
   * Closes the log-time modal and clears editor errors.
   * @returns {void} Nothing.
   */
  onLogTimeModalClose = () => {
    this.s.editingTimelogId = ""
    this.s.showLogTimeModal = false
    this.s.timelogEditorStatus = null
  }

  /**
   * Opens the log-time modal for an existing log.
   * @param {string} timelogId Timelog identifier to edit.
   * @returns {void} Nothing.
   */
  onEditPress = (timelogId) => {
    this.s.editingTimelogId = String(timelogId)
    this.s.showLogTimeModal = true
    this.s.timelogEditorStatus = null
  }

  /**
   * Creates or updates a timelog from modal values.
   * @param {{comment: string, date: string, hours: string, taskId: string, userId: string}} values Submitted modal values.
   * @returns {Promise<void>} Resolves after saving and refreshing the dashboard.
   */
  onLogTimeModalSubmit = async (values) => {
    const taskId = values.taskId.trim()
    const userId = values.userId.trim()
    if (!taskId) {
      this.s.timelogEditorStatus = {
        kind: "error",
        text: _("Select a task.")
      }
      return
    }
    if (!userId) {
      this.s.timelogEditorStatus = {
        kind: "error",
        text: _("Select a user.")
      }
      return
    }
    const timeSeconds = parseDurationSeconds(values.hours)
    const date = parseIsoDate(values.date.trim())
    if (!date) {
      this.s.timelogEditorStatus = {
        kind: "error",
        text: _("Select a date.")
      }
      return
    }
    if (!timeSeconds || timeSeconds <= 0) {
      this.s.timelogEditorStatus = {
        kind: "error",
        text: _("Enter a duration like 1h 5m, 30s or 1.5")
      }
      return
    }
    this.s.savingTimelog = true
    this.s.timelogEditorStatus = null
    try {
      const attributes = {
        comment: values.comment || "",
        date,
        taskId,
        timeSeconds,
        userId
      }
      if (this.s.editingTimelogId) {
        const timelog = await Timelog.find(this.s.editingTimelogId)
        if (!timelog) {
          throw new Error(_("Timelog not found."))
        }
        await timelog.update(attributes)
      } else {
        await Timelog.create(attributes)
      }
      const requestId = ++this.loadRequestId
      this.s.actionStatus = null
      this.s.editingTimelogId = ""
      this.s.savingTimelog = false
      this.s.showLogTimeModal = false
      this.s.timelogEditorStatus = null
      await this.loadDashboard(requestId, this.s.filters)
    } catch (error) {
      this.s.savingTimelog = false
      this.s.timelogEditorStatus = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Marks all currently loaded timelogs as invoiced.
   * @returns {Promise<void>} Resolves after the bulk action finishes.
   */
  onMarkInvoicedPress = async () => {
    const timelogIds = this.s.timelogs.map((timelog) => String(timelogId(timelog)))
    if (timelogIds.length === 0) {
      return
    }
    this.s.actionStatus = null
    this.s.markingInvoiced = true
    try {
      await Timelog.markInvoiced({
        timelogIds
      })
      const requestId = ++this.loadRequestId
      this.s.markingInvoiced = false
      await this.loadDashboard(requestId, this.s.filters)
    } catch (error) {
      this.s.actionStatus = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.markingInvoiced = false
    }
  }

  /**
   * Opens delete confirmation for a timelog.
   * @param {string} timelogId Timelog identifier to delete.
   * @returns {void} Nothing.
   */
  onDeletePress = (timelogId) => {
    this.s.actionStatus = null
    this.s.confirmingDeleteTimelogId = String(timelogId)
  }

  /**
   * Cancels the delete confirmation modal.
   * @returns {void} Nothing.
   */
  onDeleteConfirmCancelPress = () => {
    this.s.confirmingDeleteTimelogId = ""
    this.s.deletingTimelog = false
  }

  /**
   * Deletes the confirmed timelog and reloads dashboard data.
   * @returns {Promise<void>} Resolves after deletion handling finishes.
   */
  onDeleteConfirmPress = async () => {
    const confirmingDeleteTimelogId = this.s.confirmingDeleteTimelogId
    if (!confirmingDeleteTimelogId) {
      return
    }
    this.s.actionStatus = null
    this.s.deletingTimelog = true
    try {
      const timelog = await Timelog.find(confirmingDeleteTimelogId)
      if (!timelog) {
        throw new Error(_("Timelog not found."))
      }
      await timelog.destroy()
      const requestId = ++this.loadRequestId
      this.s.confirmingDeleteTimelogId = ""
      this.s.deletingTimelog = false
      await this.loadDashboard(requestId, this.s.filters)
    } catch (error) {
      this.s.actionStatus = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.deletingTimelog = false
    }
  }

  /**
   * Opens the date range modal.
   * @returns {void} Nothing.
   */
  onDateRangePress = () => {
    this.s.showDateRangeModal = true
  }

  /**
   * Closes the date range modal.
   * @returns {void} Nothing.
   */
  onDateRangeClose = () => {
    this.s.showDateRangeModal = false
  }

  /**
   * Opens the more-filters modal.
   * @returns {void} Nothing.
   */
  onMoreFiltersPress = () => {
    this.s.showMoreFiltersModal = true
  }

  /**
   * Closes the more-filters modal.
   * @returns {void} Nothing.
   */
  onMoreFiltersClose = () => {
    this.s.showMoreFiltersModal = false
  }

  /**
   * Applies the selected invoice status filter.
   * @param {"all" | "only_invoiced" | "only_uninvoiced"} statusValue Selected invoice status value.
   * @returns {void} Nothing.
   */
  onMoreFiltersApply = (statusValue) => {
    const nextFilters = {
      ...this.s.filters,
      notInvoiced: false,
      onlyInvoiced: false
    }
    if (statusValue === "only_invoiced") nextFilters.onlyInvoiced = true
    if (statusValue === "only_uninvoiced") nextFilters.notInvoiced = true
    this.s.showMoreFiltersModal = false
    this.navigateToFilters(nextFilters)
  }

  /**
   * Clears the invoice status filter.
   * @returns {void} Nothing.
   */
  onStatusFilterClearPress = () => {
    this.navigateToFilters({
      ...this.s.filters,
      notInvoiced: false,
      onlyInvoiced: false
    })
  }

  /**
   * Applies a date range from the modal.
   * @param {{dateFrom: string, dateTo: string}} range Selected date range.
   * @returns {void} Nothing.
   */
  onDateRangeApply = (range) => {
    const nextFilters = {
      ...this.s.filters,
      dateFrom: range.dateFrom || undefined,
      dateTo: range.dateTo || undefined
    }
    this.s.showDateRangeModal = false
    this.navigateToFilters(nextFilters)
  }

  /**
   * Updates the local search filter and schedules route navigation.
   * @param {string} value Search text entered by the user.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    const nextFilters = {
      ...this.s.filters,
      searchQuery: value.trim() ? value.trim() : undefined
    }
    this.s.filters = nextFilters
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.navigateToFilters(nextFilters)
    }, 500)
  }

  /**
   * Toggles a project filter.
   * @param {string} projectId Project identifier to toggle.
   * @returns {void} Nothing.
   */
  onProjectPress = (projectId) => {
    const nextFilters = {
      ...this.s.filters,
      projectIds: toggledIdList(this.s.filters.projectIds, projectId)
    }
    this.navigateToFilters(nextFilters)
  }

  /**
   * Toggles a user filter.
   * @param {string} userId User identifier to toggle.
   * @returns {void} Nothing.
   */
  onUserPress = (userId) => {
    const nextFilters = {
      ...this.s.filters,
      userIds: toggledIdList(this.s.filters.userIds, userId)
    }
    this.navigateToFilters(nextFilters)
  }

  /**
   * Updates the dashboard query params for the provided filters.
   * @param {TimelogDashboardFilters} filters Filters to serialize into the route.
   * @returns {void} Nothing.
   */
  navigateToFilters(filters) {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = null
    }
    pushQueryParamUpdates(
      this.tt.router,
      this.tt.currentQueryParams(),
      "/timelogs/dashboard",
      dashboardRouteParamUpdates(filters)
    )
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardRouteProps>} */
const TimelogsDashboardRouteComponent = memo(shapeComponent(TimelogsDashboardRoute))

export default TimelogsDashboardRouteComponent

/**
 * Builds dashboard filters from route params.
 * @param {Record<string, string | string[] | undefined>} params Route params to parse.
 * @returns {TimelogDashboardFilters} Parsed dashboard filters.
 */
function filtersFromParams(params) {
  return {
    dateFrom: routeStringParam(params, "date_gteq"),
    dateTo: routeStringParam(params, "date_lteq"),
    notInvoiced: booleanParam(params, "not_invoiced"),
    onlyInvoiced: booleanParam(params, "only_invoiced"),
    projectIds: arrayParam(params, "task_project_id_eq_any"),
    searchQuery: routeStringParam(params, "search_query"),
    userIds: arrayParam(params, "user_id_eq_any")
  }
}

/**
 * Serializes filters into a stable comparison key.
 * @param {TimelogDashboardFilters} filters Filters to serialize.
 * @returns {string} Stable serialized filter key.
 */
function serializeFilters(filters) {
  return JSON.stringify({
    dateFrom: filters.dateFrom || null,
    dateTo: filters.dateTo || null,
    notInvoiced: filters.notInvoiced,
    onlyInvoiced: filters.onlyInvoiced,
    projectIds: [...filters.projectIds].sort(),
    searchQuery: filters.searchQuery || null,
    userIds: [...filters.userIds].sort()
  })
}

/**
 * Compares filters while ignoring search text.
 * @param {TimelogDashboardFilters} currentFilters Current dashboard filters.
 * @param {TimelogDashboardFilters} nextFilters Candidate dashboard filters.
 * @returns {boolean} Whether non-search filters match.
 */
function filtersWithoutSearchMatch(currentFilters, nextFilters) {
  return (
    JSON.stringify({
      dateFrom: currentFilters.dateFrom || null,
      dateTo: currentFilters.dateTo || null,
      notInvoiced: currentFilters.notInvoiced,
      onlyInvoiced: currentFilters.onlyInvoiced,
      projectIds: [...currentFilters.projectIds].sort(),
      userIds: [...currentFilters.userIds].sort()
    }) ===
    JSON.stringify({
      dateFrom: nextFilters.dateFrom || null,
      dateTo: nextFilters.dateTo || null,
      notInvoiced: nextFilters.notInvoiced,
      onlyInvoiced: nextFilters.onlyInvoiced,
      projectIds: [...nextFilters.projectIds].sort(),
      userIds: [...nextFilters.userIds].sort()
    })
  )
}

/**
 * Converts screen filters into backend command params.
 * @param {TimelogDashboardFilters} filters Filters to send to the backend.
 * @returns {TimelogDashboardCommandParams} Command params for dashboard model methods.
 */
function dashboardCommandParams(filters) {
  return {
    ...(filters.dateFrom
      ? {
          dateFrom: filters.dateFrom
        }
      : {}),
    ...(filters.dateTo
      ? {
          dateTo: filters.dateTo
        }
      : {}),
    ...(filters.notInvoiced
      ? {
          notInvoiced: true
        }
      : {}),
    ...(filters.onlyInvoiced
      ? {
          onlyInvoiced: true
        }
      : {}),
    ...(filters.projectIds.length > 0
      ? {
          projectIds: filters.projectIds
        }
      : {}),
    ...(filters.userIds.length > 0
      ? {
          userIds: filters.userIds
        }
      : {})
  }
}

/**
 * Builds route param updates for the current dashboard filters.
 * @param {TimelogDashboardFilters} filters Filters to encode into route params.
 * @returns {Record<string, string | string[] | undefined>} Explicit query values for every dashboard filter key.
 */
function dashboardRouteParamUpdates(filters) {
  return {
    date_gteq: filters.dateFrom || undefined,
    date_lteq: filters.dateTo || undefined,
    not_invoiced: filters.notInvoiced ? "1" : undefined,
    only_invoiced: filters.onlyInvoiced ? "1" : undefined,
    search_query: filters.searchQuery || undefined,
    task_project_id_eq_any:
      filters.projectIds.length > 0 ? filters.projectIds : undefined,
    user_id_eq_any: filters.userIds.length > 0 ? filters.userIds : undefined
  }
}

/**
 * Reads a string-array route param.
 * @param {Record<string, string | string[] | undefined>} params Params to read from.
 * @param {string} key Param key to read.
 * @returns {string[]} Non-empty values for the key.
 */
function arrayParam(params, key) {
  const value = params[key]
  if (Array.isArray(value)) {
    /** @type {string[]} */
    const values = []

    for (const [index, entry] of value.entries()) {
      const stringValue = optionalString(entry, `${key}[${index}]`)
      if (stringValue) values.push(stringValue)
    }

    return values
  }
  const stringValue = optionalString(value, key)
  if (!stringValue) return []

  return [stringValue]
}

/**
 * Formats the current local date as an ISO date.
 * @returns {string} Current local date in YYYY-MM-DD format.
 */
function currentIsoDate() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
}

/**
 * Reads a single string route param.
 * @param {Record<string, string | string[] | undefined>} params Params to read from.
 * @param {string} key Param key to read.
 * @returns {string | undefined} First non-empty value for the key.
 */
function routeStringParam(params, key) {
  const value = params[key]
  if (Array.isArray(value)) {
    return value
      .map((item, index) => optionalString(item, `${key}[${index}]`))
      .find((item) => Boolean(item)) ?? undefined
  }
  return optionalString(value, key) || undefined
}

/**
 * Reads a boolean route param encoded as "1".
 * @param {Record<string, string | string[] | undefined>} params Params to read from.
 * @param {string} key Param key to read.
 * @returns {boolean} Whether the key has an enabled value.
 */
function booleanParam(params, key) {
  const value = params[key]
  if (Array.isArray(value)) {
    return value.includes("1")
  }
  return optionalString(value, key) === "1"
}

/**
 * Adds missing selected ids to a loaded option list.
 * @param {TimelogDashboardFilterOption[]} options Loaded filter options.
 * @param {string[]} selectedIds Selected identifiers that must be present.
 * @returns {TimelogDashboardFilterOption[]} Options including selected identifiers.
 */
function optionsWithSelectedIds(options, selectedIds) {
  const optionsById = new Map(options.map((option) => [option.id, option]))
  for (const selectedId of selectedIds) {
    if (!optionsById.has(selectedId)) {
      optionsById.set(selectedId, {
        id: selectedId,
        name: selectedId
      })
    }
  }
  return Array.from(optionsById.values()).sort((left, right) =>
    left.name.localeCompare(right.name)
  )
}

/**
 * Toggles an identifier in a sorted list.
 * @param {string[]} ids Current identifier list.
 * @param {string} id Identifier to toggle.
 * @returns {string[]} Updated identifier list.
 */
function toggledIdList(ids, id) {
  if (ids.includes(id)) {
    return ids.filter((value) => value !== id)
  }
  return [...ids, id].sort()
}

/**
 * Parses an ISO date string into a local Date.
 * @param {string | null} value Date string to parse.
 * @returns {Date | null} Parsed date, or null when invalid.
 */
function parseIsoDate(value) {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/**
 * Formats an ISO date with the runtime locale.
 * @param {string | null} value Date string to format.
 * @returns {string} Localized date label.
 */
function formatDate(value) {
  const date = parseIsoDate(value)
  if (!date) return value || ""
  return date.toLocaleDateString()
}

/**
 * Formats a month-year section heading.
 * @param {Date} date Date within the target month.
 * @returns {string} Localized month-year label.
 */
function monthYearLabel(date) {
  return date.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric"
  })
}

/**
 * Formats a decimal hour value.
 * @param {number} value Hour value to format.
 * @returns {string} Localized hour value.
 */
function formatHours(value) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
  }).format(value)
}

/**
 * Formats an integer value.
 * @param {number} value Number to format.
 * @returns {string} Localized integer value.
 */
function formatInteger(value) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * Formats a signed percentage value.
 * @param {number} value Percentage to format.
 * @returns {string} Percentage label.
 */
function formatPercent(value) {
  const sign = value > 0 ? "+" : ""
  return `${sign}${value}%`
}
